import type { ReactNode } from 'react';

import { cn } from '~/utils/cn';

interface AgentPanelShellProps {
  /** Accessible label for the outer `<aside>`. Defaults to "Lily assistant". */
  ariaLabel?: string;
  /** Children are the panel's interior — header / messages / input composed by the consumer. */
  children: ReactNode;
  /** Extra classes appended to the outer aside (escape hatch, prefer composing inside `children`). */
  className?: string;
  /** Automation hook on the outer `<aside>` (default `agent-panel-shell`). */
  testId?: string;
}

/**
 * Shared outer `<aside>` for every assistant panel in the app. Owns the
 * cross-feature chrome: responsive widths, border, background token. The
 * interior (header, message stream, input) stays per-consumer — both
 * `LilyAgentPanel` (chat-first) and our experiment `BaseChatPanel`
 * (scripted-first) render their composition inside this shell.
 *
 * Width scale (viewport-relative, scales naturally to display size):
 *   - phone portrait (<md):  `w-full` — panel takes all remaining width
 *     after the outer sidebar nav; pair with the consumer's collapse
 *     control so users can reveal the content lane on small screens.
 *   - tablet portrait (md, 768+):  `w-[50vw]` — half the viewport.
 *   - laptop+ (lg, 1024+):        `w-[25vw]` — quarter of the viewport.
 *
 * Uses theme tokens (`bg-background`, `border-border`) so dark mode + brand
 * remaps Just Work — replaces the earlier per-panel hardcoded `bg-white`.
 */
export function AgentPanelShell({
  ariaLabel = 'Lily assistant',
  children,
  className,
  testId = 'agent-panel-shell',
}: AgentPanelShellProps) {
  return (
    <aside
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(
        'bg-background border-border flex h-full w-full shrink-0 flex-col border-r md:w-[50vw] lg:w-[25vw]',
        className,
      )}
    >
      {children}
    </aside>
  );
}
